import axios from 'axios';
import { apiClient } from '../api';
import { Path } from '../api/backendUrl';

export interface AccountDeletionRequest {
  email: string;
  userId: string;
  scheduledDeletionDate: string;
  requestDate: string;
  status: 'pending' | 'cancelled' | 'completed';
  gracePeriodDays: number;
}

export interface AccountDeletionResponse {
  success: boolean;
  message: string;
  deletionRequest?: AccountDeletionRequest;
  errorId?: string;
}

export interface DeleteRequestResponse {
  success: boolean;
  message: string;
  errorId?: string;
  deletionToken?: string;
  securityToken?: string;
  resendCount?: number;
}

export interface ReactivationResponse {
  success: boolean;
  message: string;
  errorId?: string;
}

const USER_ENDPOINT = import.meta.env.VITE_APP_BACKEND_USER;

export const requestOtp = async (
  accountType: string,
  reason: string,
  feedback?: string
): Promise<DeleteRequestResponse> => {
  try {
    const payload: {
      accountType: string;
      reason: string;
      feedback?: string;
    } = {
      accountType,
      reason,
    };

    if (reason === 'other' && feedback !== undefined) {
      payload.feedback = feedback;
    }

    const response = await apiClient.post(
      `${USER_ENDPOINT}/${Path.deactivateRequest}`,
      payload
    );

    return {
      success: response?.data?.success ?? true,
      securityToken: response?.data?.data?.securityToken,
      message:
        response?.data?.message || 'OTP code sent. Please check your email.',
    };
  } catch (error) {
    console.error('Error requesting account deactivation:', error);

    if (axios.isAxiosError(error) && error.response?.data) {
      const backendMessage =
        error.response.data.message ||
        'Rate limit exceeded. Please try again later.';
      throw new Error(backendMessage);
    }
    throw error;
  }
};

export const confirmUserDeactivation = async (payload: {
  accountType: string;
  otp: string;
  securityToken: string;
}): Promise<DeleteRequestResponse> => {
  try {
    const response = await apiClient.post(
      `${USER_ENDPOINT}/${Path.verifyDeactivateOtp}`,
      payload
    );

    return {
      success: response?.data?.success ?? true,
      message: response?.data?.message || 'Account deactivation successful.',
    };
  } catch (error) {
    console.error('Error confirming account deactivation:', error);

    if (axios.isAxiosError(error) && error.response?.data) {
      const backendMessage =
        error.response.data.message ||
        'Rate limit exceeded. Please try again later.';
      throw new Error(backendMessage);
    }
    throw error;
  }
};

export const resendOtp = async (
  accountType: string,
  action: string
): Promise<DeleteRequestResponse> => {
  try {
    const payload = {
      accountType,
      action,
    };

    const response = await apiClient.post(
      `${USER_ENDPOINT}/${Path.resendOtpForAccount}`,
      payload
    );

    return {
      success: response?.data?.success ?? true,
      message:
        response?.data?.message ||
        'Verification code successfully sent to your email',
      resendCount: response?.data?.data?.resendCount || 0,
      securityToken: response?.data?.data?.securityToken,
    };
  } catch (error: any) {
    console.error('Error sending resend OTP code:', error);

    if (axios.isAxiosError(error) && error.response?.data) {
      const backendMessage =
        error.response.data.message ||
        'Rate limit exceeded. Please try again later.';
      throw new Error(backendMessage);
    }

    throw error;
  }
};

export const reactivateAccount = async (payload: {
  accountType: string;
  userId: string;
  email: string;
  role: string;
}): Promise<{ success: boolean }> => {
  try {
    await apiClient.post(`${USER_ENDPOINT}/${Path.reactivateAccount}`, payload);

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error reactivating account:', error);
    return {
      success: false,
    };
  }
};
